import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { providerPublicAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { toast } from 'sonner';
import { 
  Building2, DollarSign, Calendar, Users, 
  Loader2, LogOut, Clock, CheckCircle, XCircle,
  MapPin, History, TrendingUp, BanknoteIcon, AlertCircle, Wallet,
  CalendarDays, Phone, Mail, ChevronRight, RefreshCw, Eye
} from 'lucide-react';

export default function ServiceProviderDashboardPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState(null);
  const [calendarData, setCalendarData] = useState(null);
  const [earningsData, setEarningsData] = useState(null);
  const [provider, setProvider] = useState(null);
  const [activeTab, setActiveTab] = useState('calendar');
  
  // Bank info state
  const [showBankDialog, setShowBankDialog] = useState(false);
  const [savingBank, setSavingBank] = useState(false);
  const [bankInfo, setBankInfo] = useState({
    bank_name: '',
    account_holder: '',
    account_number: '',
    routing_number: '',
    account_type: 'checking',
  });
  
  // Booking detail dialog
  const [selectedBooking, setSelectedBooking] = useState(null);

  useEffect(() => {
    // Check if logged in
    const token = localStorage.getItem('service_provider_token');
    const providerData = localStorage.getItem('service_provider');
    
    if (!token || !providerData) {
      navigate('/service-providers');
      return;
    }
    
    setProvider(JSON.parse(providerData));
    loadAllData();
  }, [navigate]);

  const loadAllData = useCallback(async () => {
    try {
      const [dashboardRes, calendarRes, earningsRes] = await Promise.all([
        providerPublicAPI.getDashboard(),
        providerPublicAPI.getCalendar(),
        providerPublicAPI.getEarnings()
      ]);
      
      setDashboardData(dashboardRes.data);
      setCalendarData(calendarRes.data);
      setEarningsData(earningsRes.data);
      
      // Load bank info if available
      if (dashboardRes.data?.provider?.bank_info) {
        setBankInfo(dashboardRes.data.provider.bank_info);
      }
    } catch (error) {
      console.error('Error loading dashboard:', error);
      if (error.response?.status === 401) {
        localStorage.removeItem('service_provider_token');
        localStorage.removeItem('service_provider');
        navigate('/service-providers');
      }
      toast.error('Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  const handleLogout = () => {
    localStorage.removeItem('service_provider_token');
    localStorage.removeItem('service_provider');
    navigate('/service-providers');
    toast.success('Logged out successfully');
  };

  const saveBankInfo = async () => {
    if (!bankInfo.bank_name || !bankInfo.account_holder || !bankInfo.account_number) {
      toast.error('Please fill in required bank details');
      return;
    }
    
    setSavingBank(true);
    try {
      await providerPublicAPI.updateBankInfo(bankInfo);
      toast.success('Bank information saved successfully');
      setShowBankDialog(false);
      loadAllData();
    } catch (error) {
      toast.error('Failed to save bank information');
    } finally {
      setSavingBank(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(price || 0);
  };

  const isToday = (dateStr) => {
    const today = new Date().toISOString().split('T')[0];
    return dateStr === today;
  };

  const isTomorrow = (dateStr) => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    return dateStr === tomorrow.toISOString().split('T')[0];
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100">
        <div className="text-center">
          <Loader2 className="h-12 w-12 animate-spin text-blue-600 mx-auto" />
          <p className="mt-4 text-gray-600">Loading your dashboard...</p>
        </div>
      </div>
    );
  }

  const stats = dashboardData?.stats || {};
  const tours = dashboardData?.tours || [];
  const providerInfo = dashboardData?.provider || provider;
  const calendar = calendarData?.calendar || {};
  const earnings = earningsData || {};

  // Get upcoming dates from calendar
  const today = new Date().toISOString().split('T')[0];
  const upcomingDates = Object.entries(calendar)
    .filter(([date]) => date >= today)
    .sort(([a], [b]) => a.localeCompare(b));

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-md border-b sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to="/" className="flex items-center gap-2">
              <div className="p-2 bg-blue-600 rounded-lg">
                <Building2 className="h-5 w-5 text-white" />
              </div>
              <span className="font-bold text-xl text-gray-900">Provider Portal</span>
            </Link>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-sm text-gray-600">
              Welcome, <strong className="text-gray-900">{providerInfo?.company_name || providerInfo?.name}</strong>
            </span>
            <Button variant="outline" size="sm" onClick={() => loadAllData()} className="gap-2">
              <RefreshCw className="h-4 w-4" />
              Refresh
            </Button>
            <Button variant="outline" size="sm" onClick={handleLogout} className="gap-2 text-red-600 hover:text-red-700 hover:bg-red-50">
              <LogOut className="h-4 w-4" />
              Logout
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-8">
        {/* Hero Stats */}
        <div className="grid md:grid-cols-5 gap-4 mb-8">
          <Card className="p-5 bg-gradient-to-br from-blue-500 to-blue-600 text-white">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-white/20 rounded-xl">
                <CalendarDays className="h-6 w-6" />
              </div>
              <div>
                <p className="text-blue-100 text-sm">Upcoming Bookings</p>
                <p className="text-3xl font-bold">{earnings.upcoming_bookings || calendarData?.total_bookings || 0}</p>
              </div>
            </div>
          </Card>
          
          <Card className="p-5 bg-gradient-to-br from-green-500 to-emerald-600 text-white">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-white/20 rounded-xl">
                <DollarSign className="h-6 w-6" />
              </div>
              <div>
                <p className="text-green-100 text-sm">Total Earned</p>
                <p className="text-3xl font-bold">{formatPrice(earnings.total_earned)}</p>
              </div>
            </div>
          </Card>
          
          <Card className="p-5 bg-gradient-to-br from-amber-500 to-orange-500 text-white">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-white/20 rounded-xl">
                <Clock className="h-6 w-6" />
              </div>
              <div>
                <p className="text-amber-100 text-sm">Upcoming Earnings</p>
                <p className="text-3xl font-bold">{formatPrice(earnings.upcoming_earnings)}</p>
              </div>
            </div>
          </Card>
          
          <Card className="p-5 bg-gradient-to-br from-purple-500 to-violet-600 text-white">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-white/20 rounded-xl">
                <CheckCircle className="h-6 w-6" />
              </div>
              <div>
                <p className="text-purple-100 text-sm">Completed Tours</p>
                <p className="text-3xl font-bold">{earnings.completed_bookings || 0}</p>
              </div>
            </div>
          </Card>
          
          <Card className="p-5 bg-gradient-to-br from-red-500 to-rose-600 text-white">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-white/20 rounded-xl">
                <Wallet className="h-6 w-6" />
              </div>
              <div>
                <p className="text-red-100 text-sm">Balance Owed</p>
                <p className="text-3xl font-bold">{formatPrice(earnings.balance_owed)}</p>
              </div>
            </div>
          </Card>
        </div>

        {/* Main Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid grid-cols-4 w-fit mb-6 bg-white shadow-sm">
            <TabsTrigger value="calendar" className="gap-2 data-[state=active]:bg-blue-50 data-[state=active]:text-blue-700">
              <CalendarDays className="h-4 w-4" />
              Calendar
            </TabsTrigger>
            <TabsTrigger value="earnings" className="gap-2 data-[state=active]:bg-green-50 data-[state=active]:text-green-700">
              <DollarSign className="h-4 w-4" />
              Earnings
            </TabsTrigger>
            <TabsTrigger value="tours" className="gap-2 data-[state=active]:bg-purple-50 data-[state=active]:text-purple-700">
              <MapPin className="h-4 w-4" />
              My Tours
            </TabsTrigger>
            <TabsTrigger value="account" className="gap-2 data-[state=active]:bg-gray-100">
              <Building2 className="h-4 w-4" />
              Account
            </TabsTrigger>
          </TabsList>

          {/* Calendar Tab */}
          <TabsContent value="calendar">
            <div className="space-y-6">
              {/* Summary Banner */}
              <Card className="p-4 bg-blue-50 border-blue-200">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Calendar className="h-6 w-6 text-blue-600" />
                    <div>
                      <p className="font-semibold text-blue-900">Your Upcoming Schedule</p>
                      <p className="text-sm text-blue-700">
                        {calendarData?.total_bookings || 0} bookings • {calendarData?.total_guests || 0} total guests
                      </p>
                    </div>
                  </div>
                </div>
              </Card>

              {/* Calendar Days */}
              {upcomingDates.length === 0 ? (
                <Card className="p-12 text-center">
                  <CalendarDays className="h-16 w-16 mx-auto mb-4 text-gray-300" />
                  <h3 className="font-semibold text-xl text-gray-600 mb-2">No Upcoming Bookings</h3>
                  <p className="text-gray-500">When admin assigns bookings to you, they will appear here.</p>
                </Card>
              ) : (
                <div className="space-y-4">
                  {upcomingDates.map(([date, data]) => (
                    <Card key={date} className={`overflow-hidden ${isToday(date) ? 'ring-2 ring-blue-500' : isTomorrow(date) ? 'ring-2 ring-amber-500' : ''}`}>
                      {/* Date Header */}
                      <div className={`px-5 py-3 flex items-center justify-between ${
                        isToday(date) ? 'bg-blue-600 text-white' : 
                        isTomorrow(date) ? 'bg-amber-500 text-white' : 
                        'bg-gray-100 text-gray-800'
                      }`}>
                        <div className="flex items-center gap-3">
                          <CalendarDays className="h-5 w-5" />
                          <div>
                            <p className="font-bold text-lg">{formatDate(date)}</p>
                            {isToday(date) && <Badge className="bg-white/20 text-white">TODAY</Badge>}
                            {isTomorrow(date) && <Badge className="bg-white/20 text-white">TOMORROW</Badge>}
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="font-bold">{data.bookings?.length || 0} booking{(data.bookings?.length || 0) !== 1 ? 's' : ''}</p>
                          <p className="text-sm opacity-80">{data.total_guests || 0} guests</p>
                        </div>
                      </div>
                      
                      {/* Bookings List */}
                      <div className="divide-y">
                        {(data.bookings || []).map((booking, idx) => (
                          <div 
                            key={booking.id || idx} 
                            className="p-4 hover:bg-gray-50 cursor-pointer transition-colors"
                            onClick={() => setSelectedBooking(booking)}
                          >
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-4">
                                <div className="text-center min-w-[70px] p-2 bg-blue-100 rounded-lg">
                                  <p className="font-bold text-blue-700 text-lg">{booking.time || 'TBD'}</p>
                                </div>
                                <div>
                                  <p className="font-bold text-lg text-gray-900">{booking.customer_name}</p>
                                  <p className="text-gray-600">
                                    {booking.guests} guest{booking.guests !== 1 ? 's' : ''} • {booking.tour_title}
                                  </p>
                                  {booking.pickup && (
                                    <p className="text-sm text-gray-500 flex items-center gap-1 mt-1">
                                      <MapPin className="h-3 w-3" />
                                      Pickup: {booking.pickup}
                                    </p>
                                  )}
                                </div>
                              </div>
                              <div className="flex items-center gap-3">
                                <div className="text-right">
                                  <p className="font-bold text-xl text-green-600">{formatPrice(booking.provider_earnings)}</p>
                                  <p className="text-xs text-gray-500">Your earnings</p>
                                </div>
                                <ChevronRight className="h-5 w-5 text-gray-400" />
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </Card>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>

          {/* Earnings Tab */}
          <TabsContent value="earnings">
            <div className="space-y-6">
              {/* Earnings Summary */}
              <Card className="p-6 bg-gradient-to-r from-green-50 to-emerald-50">
                <h3 className="font-bold text-xl mb-4 text-green-800">Earnings Summary</h3>
                <div className="grid md:grid-cols-4 gap-6">
                  <div>
                    <p className="text-sm text-gray-600">Total Earned (Completed)</p>
                    <p className="text-3xl font-bold text-green-600">{formatPrice(earnings.total_earned)}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Already Paid</p>
                    <p className="text-3xl font-bold text-blue-600">{formatPrice(earnings.total_paid)}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Balance Owed to You</p>
                    <p className="text-3xl font-bold text-red-600">{formatPrice(earnings.balance_owed)}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Upcoming Earnings</p>
                    <p className="text-3xl font-bold text-amber-600">{formatPrice(earnings.upcoming_earnings)}</p>
                  </div>
                </div>
              </Card>

              {/* Recent Earnings */}
              <Card className="p-6">
                <h3 className="font-bold text-lg mb-4">Recent Completed Tours</h3>
                {(earnings.recent_earnings || []).length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <DollarSign className="h-12 w-12 mx-auto mb-3 opacity-30" />
                    <p>No completed tours yet</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {earnings.recent_earnings.map((item, idx) => (
                      <div key={idx} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                        <div>
                          <p className="font-semibold">{item.tour_title}</p>
                          <p className="text-sm text-gray-500">
                            {formatDate(item.date)} • {item.guests} guest{item.guests !== 1 ? 's' : ''}
                          </p>
                        </div>
                        <p className="font-bold text-lg text-green-600">{formatPrice(item.earnings)}</p>
                      </div>
                    ))}
                  </div>
                )}
              </Card>
            </div>
          </TabsContent>

          {/* My Tours Tab */}
          <TabsContent value="tours">
            <Card className="p-6">
              <h3 className="font-bold text-xl mb-6">Tours Assigned to You</h3>
              
              {tours.length === 0 ? (
                <div className="text-center py-12">
                  <MapPin className="h-16 w-16 mx-auto mb-4 text-gray-300" />
                  <h4 className="font-semibold text-xl text-gray-600 mb-2">No Tours Assigned</h4>
                  <p className="text-gray-500">Contact the admin to get tours assigned to your account.</p>
                </div>
              ) : (
                <div className="grid md:grid-cols-2 gap-6">
                  {tours.map((tour) => (
                    <div key={tour.id} className="border rounded-xl overflow-hidden hover:shadow-lg transition-shadow">
                      {tour.images?.[0] && (
                        <img src={tour.images[0]} alt={tour.title} className="w-full h-40 object-cover" />
                      )}
                      <div className="p-4">
                        <h4 className="font-bold text-lg mb-1">{tour.title}</h4>
                        <p className="text-gray-500 text-sm mb-3">{tour.duration}</p>
                        <div className="flex items-center justify-between">
                          <p className="text-2xl font-bold text-blue-600">${tour.price}</p>
                          <Badge variant="outline" className="text-green-600 border-green-600">Active</Badge>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </Card>
          </TabsContent>

          {/* Account Tab */}
          <TabsContent value="account">
            <div className="grid md:grid-cols-2 gap-6">
              <Card className="p-6">
                <h2 className="font-bold text-xl flex items-center gap-2 mb-6">
                  <Building2 className="h-5 w-5 text-blue-600" />
                  Account Information
                </h2>
                <div className="space-y-4">
                  <div className="flex justify-between items-center py-3 border-b">
                    <span className="text-gray-600">Name</span>
                    <span className="font-semibold">{providerInfo?.name}</span>
                  </div>
                  {providerInfo?.company_name && (
                    <div className="flex justify-between items-center py-3 border-b">
                      <span className="text-gray-600">Company</span>
                      <span className="font-semibold">{providerInfo.company_name}</span>
                    </div>
                  )}
                  <div className="flex justify-between items-center py-3 border-b">
                    <span className="text-gray-600">Email</span>
                    <span className="font-semibold">{providerInfo?.email}</span>
                  </div>
                  <div className="flex justify-between items-center py-3 border-b">
                    <span className="text-gray-600">Active Tours</span>
                    <span className="font-semibold">{tours.length}</span>
                  </div>
                  <div className="flex justify-between items-center py-3">
                    <span className="text-gray-600">Total Bookings</span>
                    <span className="font-semibold">{stats.total_bookings || 0}</span>
                  </div>
                </div>
              </Card>

              <Card className="p-6">
                <div className="flex justify-between items-start mb-6">
                  <h2 className="font-bold text-xl flex items-center gap-2">
                    <BanknoteIcon className="h-5 w-5 text-green-600" />
                    Payout Information
                  </h2>
                  <Button variant="outline" size="sm" onClick={() => setShowBankDialog(true)}>
                    {bankInfo.bank_name ? 'Edit' : 'Add'} Bank Info
                  </Button>
                </div>
                
                {bankInfo.bank_name ? (
                  <div className="space-y-4">
                    <div className="flex justify-between items-center py-3 border-b">
                      <span className="text-gray-600">Bank</span>
                      <span className="font-semibold">{bankInfo.bank_name}</span>
                    </div>
                    <div className="flex justify-between items-center py-3 border-b">
                      <span className="text-gray-600">Account Holder</span>
                      <span className="font-semibold">{bankInfo.account_holder}</span>
                    </div>
                    <div className="flex justify-between items-center py-3 border-b">
                      <span className="text-gray-600">Account</span>
                      <span className="font-semibold">****{bankInfo.account_number?.slice(-4)}</span>
                    </div>
                    <div className="flex justify-between items-center py-3">
                      <span className="text-gray-600">Type</span>
                      <span className="font-semibold capitalize">{bankInfo.account_type}</span>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <AlertCircle className="h-12 w-12 mx-auto mb-3 text-amber-500" />
                    <p className="font-semibold text-gray-700 mb-1">Bank Details Required</p>
                    <p className="text-gray-500 text-sm">Add your bank details to receive payouts</p>
                  </div>
                )}
              </Card>
            </div>
          </TabsContent>
        </Tabs>
      </main>

      {/* Booking Detail Dialog */}
      <Dialog open={!!selectedBooking} onOpenChange={(open) => !open && setSelectedBooking(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Users className="h-5 w-5 text-blue-600" />
              Booking Details
            </DialogTitle>
          </DialogHeader>
          {selectedBooking && (
            <div className="space-y-4 py-4">
              <div className="bg-blue-50 p-4 rounded-lg">
                <p className="font-bold text-lg text-blue-900">{selectedBooking.tour_title}</p>
                <p className="text-blue-700">Reference: #{selectedBooking.reference_number || 'N/A'}</p>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-500">Customer</p>
                  <p className="font-semibold text-lg">{selectedBooking.customer_name}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Guests</p>
                  <p className="font-semibold text-lg">{selectedBooking.guests}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Time</p>
                  <p className="font-semibold text-lg">{selectedBooking.time || 'TBD'}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Your Earnings</p>
                  <p className="font-bold text-lg text-green-600">{formatPrice(selectedBooking.provider_earnings)}</p>
                </div>
              </div>
              
              {selectedBooking.customer_phone && (
                <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg">
                  <Phone className="h-4 w-4 text-gray-500" />
                  <span className="font-medium">{selectedBooking.customer_phone}</span>
                  <a href={`tel:${selectedBooking.customer_phone}`} className="ml-auto">
                    <Button size="sm" variant="outline">Call</Button>
                  </a>
                </div>
              )}
              
              {selectedBooking.pickup && (
                <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg">
                  <MapPin className="h-4 w-4 text-gray-500" />
                  <div>
                    <p className="text-sm text-gray-500">Pickup Location</p>
                    <p className="font-medium">{selectedBooking.pickup}</p>
                  </div>
                </div>
              )}
              
              {selectedBooking.special_requests && (
                <div className="p-3 bg-amber-50 rounded-lg border border-amber-200">
                  <p className="text-sm font-medium text-amber-800">Special Requests:</p>
                  <p className="text-amber-700">{selectedBooking.special_requests}</p>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setSelectedBooking(null)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bank Info Dialog */}
      <Dialog open={showBankDialog} onOpenChange={setShowBankDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <BanknoteIcon className="h-5 w-5 text-green-600" />
              Bank Account Information
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label>Bank Name *</Label>
              <Input
                value={bankInfo.bank_name}
                onChange={(e) => setBankInfo({ ...bankInfo, bank_name: e.target.value })}
                placeholder="e.g., Bank of America"
              />
            </div>
            
            <div>
              <Label>Account Holder Name *</Label>
              <Input
                value={bankInfo.account_holder}
                onChange={(e) => setBankInfo({ ...bankInfo, account_holder: e.target.value })}
                placeholder="Full name on account"
              />
            </div>
            
            <div>
              <Label>Account Number *</Label>
              <Input
                value={bankInfo.account_number}
                onChange={(e) => setBankInfo({ ...bankInfo, account_number: e.target.value })}
                placeholder="Account number"
              />
            </div>
            
            <div>
              <Label>Routing Number</Label>
              <Input
                value={bankInfo.routing_number}
                onChange={(e) => setBankInfo({ ...bankInfo, routing_number: e.target.value })}
                placeholder="9-digit routing number"
              />
            </div>
            
            <div>
              <Label>Account Type</Label>
              <div className="flex gap-4 mt-2">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    checked={bankInfo.account_type === 'checking'}
                    onChange={() => setBankInfo({ ...bankInfo, account_type: 'checking' })}
                    className="w-4 h-4"
                  />
                  Checking
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    checked={bankInfo.account_type === 'savings'}
                    onChange={() => setBankInfo({ ...bankInfo, account_type: 'savings' })}
                    className="w-4 h-4"
                  />
                  Savings
                </label>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowBankDialog(false)}>Cancel</Button>
            <Button onClick={saveBankInfo} disabled={savingBank} className="bg-green-600 hover:bg-green-700">
              {savingBank && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              Save Bank Info
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
